import {
  NotificationResponseByType,
  NotificationResponseTypes,
  NotificationSocketResponses,
} from '@api';
import { LogLevel } from '@grafana/faro-react';
import { config } from '@terminal/config';
import { Tracking } from '@terminal/services/tracking';
import { useBaseWs } from '@terminal/services/use-base-ws/useBaseWs';
import {
  CloseReason,
  WebSocketStatus,
} from '@terminal/services/use-base-ws/utils';

const RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY_MS = 10_000;

export interface NotificationWsInstance {
  subscribe<T extends NotificationResponseTypes>(
    type: T,
    callback: (
      response: NotificationResponseByType<T>,
      isActiveTab: boolean
    ) => void
  ): () => void;
  unsubscribe: () => void;
  readonly isConnected: boolean;
  readonly status: WebSocketStatus;
  onmessage: ((message: NotificationSocketResponses) => void) | null;
  onVisibilityChange?: (e: Event) => void;
}

export type CreateNotificationWsOptions = {
  onError?: (error: string) => void;
};

export const createNotificationWs = (
  options: CreateNotificationWsOptions = {}
): NotificationWsInstance => {
  const { onError } = options;
  const url = config.api.wsNotifierRoot;

  let isTabActive = true;
  const listeners = new Map<
    NotificationResponseTypes,
    ((v: NotificationSocketResponses, isTabActive: boolean) => void)[]
  >();
  let customOnMessage: ((message: NotificationSocketResponses) => void) | null =
    null;

  const dispatchMessage = (message: NotificationSocketResponses) => {
    const current = listeners.get(message.event.type) ?? [];
    current.forEach(listener => listener(message, isTabActive));
  };

  let lifecycleListenersAttached = false;

  const reconnectIfClosed = () => {
    const isClosed =
      base.status === WebSocketStatus.CLOSED ||
      base.status === WebSocketStatus.UNINSTANTIATED;

    if (isClosed) {
      Tracking.captureMessage(
        'Notifier websocket reconnecting after browser recovery',
        LogLevel.WARN
      );
      base.connect();
    }
  };

  const visibilityChange = () => {
    const isAlive =
      base.status !== WebSocketStatus.CLOSED &&
      base.status !== WebSocketStatus.UNINSTANTIATED;
    isTabActive = !document.hidden && isAlive;

    if (!document.hidden && !isAlive) {
      reconnectIfClosed();
    }

    instance.onVisibilityChange?.(new Event('visibilitychange'));
  };

  const attachLifecycleListeners = () => {
    if (lifecycleListenersAttached) {
      return;
    }

    document.addEventListener('visibilitychange', visibilityChange);
    window.addEventListener('online', reconnectIfClosed);
    lifecycleListenersAttached = true;
  };

  const removeLifecycleListeners = () => {
    document.removeEventListener('visibilitychange', visibilityChange);
    window.removeEventListener('online', reconnectIfClosed);
    lifecycleListenersAttached = false;
  };

  const base = useBaseWs(url, {
    onOpen: () => {
      attachLifecycleListeners();
    },
    onClose: (event: CloseEvent) => {
      if (event.reason === CloseReason.NoConnectionRequired) {
        removeLifecycleListeners();
      }
    },
    onMessage: (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as NotificationSocketResponses;
        dispatchMessage(message);
        customOnMessage?.(message);
      } catch (_error) {
        onError?.('WebSocket: Failed to parse message');
      }
    },
    onError: () => {
      Tracking.captureMessage(
        'Notifier websocket transport error',
        LogLevel.WARN
      );
      onError?.('WebSocket: Transport error');
    },
    onReconnectStop: attempts => {
      Tracking.captureMessage(
        `Notifier websocket reconnect stopped after ${attempts} attempts`,
        LogLevel.ERROR
      );
    },
    shouldReconnect: () => true,
    reconnectAttempts: RECONNECT_ATTEMPTS,
    reconnectInterval: attempt =>
      Math.min((attempt + 1) * 1_000, MAX_RECONNECT_DELAY_MS),
    retryOnError: true,
  });

  const instance: NotificationWsInstance = {
    subscribe<T extends NotificationResponseTypes>(
      type: T,
      callback: (
        response: NotificationResponseByType<T>,
        isActiveTab: boolean
      ) => void
    ) {
      const current = listeners.get(type) ?? [];
      listeners.set(type, [
        ...current,
        callback as (
          v: NotificationSocketResponses,
          isTabActive: boolean
        ) => void,
      ]);

      return () => {
        const current = listeners.get(type) ?? [];
        listeners.set(
          type,
          current.filter(c => c !== callback)
        );
      };
    },
    unsubscribe: () => {
      base.disconnect(1000, CloseReason.NoConnectionRequired);
    },
    get isConnected() {
      return base.isConnected;
    },
    get status() {
      return base.status;
    },
    get onmessage() {
      return customOnMessage;
    },
    set onmessage(fn: ((message: NotificationSocketResponses) => void) | null) {
      customOnMessage = fn;
    },
    onVisibilityChange: undefined,
  };

  return instance;
};
